import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Switch } from './ui/switch';
import { useToast } from '../hooks/use-toast';
import { 
  FileText, Plus, Clock, CheckCircle, XCircle, AlertTriangle, 
  Flame, Shield, Lock, Shovel, Construction, Zap, Truck
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PermitToWorkPage = () => {
  const [permits, setPermits] = useState([]);
  const [permitTypes, setPermitTypes] = useState({});
  const [activePermits, setActivePermits] = useState([]);
  const [pendingPermits, setPendingPermits] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedPermit, setSelectedPermit] = useState(null);
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    permit_type: 'hot_work',
    title: '',
    description: '',
    location: '',
    project_name: '',
    requestor_name: '',
    work_start: '',
    work_end: '',
    workers: [],
    supervisor: '',
    emergency_contact: '',
    emergency_phone: '',
    ppe_required: [],
    precautions: []
  });

  const [approvalData, setApprovalData] = useState({
    action: 'approve',
    approver_name: '',
    approver_role: 'supervisor',
    comments: ''
  });

  const [newWorker, setNewWorker] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [permitsRes, typesRes, activeRes, pendingRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/permits`),
        axios.get(`${API}/api/permits/types`),
        axios.get(`${API}/api/permits/active`),
        axios.get(`${API}/api/permits/pending-approval`),
        axios.get(`${API}/api/permits/stats/summary`)
      ]);
      setPermits(permitsRes.data.permits || []);
      setPermitTypes(typesRes.data.permit_types || {});
      setActivePermits(activeRes.data.active_permits || []);
      setPendingPermits(pendingRes.data.pending_permits || []);
      setStats(statsRes.data);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePermit = async (e) => {
    e.preventDefault();
    try {
      const config = permitTypes[formData.permit_type];
      const payload = {
        ...formData,
        ppe_required: formData.ppe_required.length > 0 ? formData.ppe_required : config?.default_ppe || []
      };
      await axios.post(`${API}/api/permits`, payload);
      toast({ title: 'Success', description: 'Permit created!' });
      setShowCreateForm(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to create permit', variant: 'destructive' });
    }
  };

  const resetForm = () => {
    setFormData({
      permit_type: 'hot_work',
      title: '',
      description: '',
      location: '',
      project_name: '',
      requestor_name: '',
      work_start: '',
      work_end: '',
      workers: [],
      supervisor: '',
      emergency_contact: '',
      emergency_phone: '',
      ppe_required: [],
      precautions: []
    });
    setNewWorker('');
  };

  const submitForApproval = async (permitId) => {
    try {
      await axios.post(`${API}/api/permits/${permitId}/submit`);
      toast({ title: 'Submitted', description: 'Permit submitted for approval' });
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to submit', variant: 'destructive' });
    }
  };

  const handleApproval = async () => {
    if (!selectedPermit) return;
    try {
      await axios.post(`${API}/api/permits/${selectedPermit.id}/approve`, approvalData);
      toast({ title: 'Success', description: `Permit ${approvalData.action}d` });
      setShowApprovalDialog(false);
      setSelectedPermit(null);
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to process approval', variant: 'destructive' });
    }
  };

  const activatePermit = async (permitId) => {
    try {
      await axios.post(`${API}/api/permits/${permitId}/activate`);
      toast({ title: 'Activated', description: 'Permit is now active' });
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to activate', variant: 'destructive' });
    }
  };

  const closePermit = async (permitId) => {
    try {
      await axios.post(`${API}/api/permits/${permitId}/close?closed_by=Admin`);
      toast({ title: 'Closed', description: 'Permit has been closed' });
      fetchData();
      setSelectedPermit(null);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to close', variant: 'destructive' });
    }
  };

  const addWorker = () => {
    if (newWorker.trim()) {
      setFormData({ ...formData, workers: [...formData.workers, newWorker.trim()] });
      setNewWorker('');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'approved': return 'bg-blue-100 text-blue-800';
      case 'pending_approval': return 'bg-yellow-100 text-yellow-800';
      case 'draft': return 'bg-gray-100 text-gray-800';
      case 'closed': return 'bg-purple-100 text-purple-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'expired': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPermitIcon = (type) => {
    const icons = {
      hot_work: Flame,
      confined_space: Shield,
      loto: Lock,
      excavation: Shovel,
      working_at_height: Construction,
      electrical: Zap,
      crane_lift: Truck
    };
    const Icon = icons[type] || FileText;
    return <Icon className="w-5 h-5" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Permit to Work System</h1>
          <p className="text-gray-600">Manage high-risk work permits with approval workflow</p>
        </div>
        <Button onClick={() => setShowCreateForm(true)} className="bg-blue-600 hover:bg-blue-700">
          <Plus className="w-4 h-4 mr-2" /> New Permit
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-2xl font-bold text-blue-600">{stats?.total_permits || 0}</p>
              <p className="text-sm text-gray-600">Total</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-green-200 bg-green-50">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">{stats?.active || 0}</p>
              <p className="text-sm text-gray-600">Active</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-yellow-200 bg-yellow-50">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-2xl font-bold text-yellow-600">{stats?.pending_approval || 0}</p>
              <p className="text-sm text-gray-600">Pending</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-2xl font-bold text-purple-600">{stats?.closed || 0}</p>
              <p className="text-sm text-gray-600">Closed</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-2xl font-bold text-red-600">{stats?.rejected || 0}</p>
              <p className="text-sm text-gray-600">Rejected</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Active Permits Alert */}
      {activePermits.length > 0 && (
        <Card className="border-green-300 bg-green-50">
          <CardHeader>
            <CardTitle className="flex items-center text-green-800">
              <CheckCircle className="w-5 h-5 mr-2" /> Active Permits ({activePermits.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {activePermits.map((permit) => (
                <div key={permit.id} className="flex items-center justify-between p-3 bg-white rounded-lg shadow-sm">
                  <div className="flex items-center gap-3">
                    {getPermitIcon(permit.permit_type)}
                    <div>
                      <p className="font-medium">{permit.title}</p>
                      <p className="text-sm text-gray-600">{permit.location}</p>
                    </div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => setSelectedPermit(permit)}>View</Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pending Approvals Alert */}
      {pendingPermits.length > 0 && (
        <Card className="border-yellow-300 bg-yellow-50">
          <CardHeader>
            <CardTitle className="flex items-center text-yellow-800">
              <Clock className="w-5 h-5 mr-2" /> Pending Approval ({pendingPermits.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {pendingPermits.map((permit) => (
                <div key={permit.id} className="flex items-center justify-between p-3 bg-white rounded-lg shadow-sm">
                  <div className="flex items-center gap-3">
                    {getPermitIcon(permit.permit_type)}
                    <div>
                      <p className="font-medium">{permit.title}</p>
                      <p className="text-sm text-gray-600">{permit.requestor_name} • {permit.location}</p>
                    </div>
                  </div>
                  <Button size="sm" onClick={() => { setSelectedPermit(permit); setShowApprovalDialog(true); }}>Review</Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="all" className="w-full">
        <TabsList>
          <TabsTrigger value="all">All Permits ({permits.length})</TabsTrigger>
          <TabsTrigger value="types">Permit Types</TabsTrigger>
        </TabsList>

        <TabsContent value="all" className="space-y-4">
          {permits.map((permit) => (
            <Card key={permit.id} className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => setSelectedPermit(permit)}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-gray-100 rounded-lg">
                      {getPermitIcon(permit.permit_type)}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={getStatusColor(permit.status)}>{permit.status?.replace('_', ' ')}</Badge>
                        <span className="text-sm text-gray-500">{permit.permit_number}</span>
                      </div>
                      <h3 className="font-semibold">{permit.title}</h3>
                      <p className="text-sm text-gray-600">{permit.location} • {permit.supervisor}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {new Date(permit.work_start).toLocaleString()} - {new Date(permit.work_end).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  {permit.status === 'draft' && (
                    <Button size="sm" onClick={(e) => { e.stopPropagation(); submitForApproval(permit.id); }}>Submit</Button>
                  )}
                  {permit.status === 'approved' && (
                    <Button size="sm" className="bg-green-600" onClick={(e) => { e.stopPropagation(); activatePermit(permit.id); }}>Activate</Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
          {permits.length === 0 && (
            <Card>
              <CardContent className="text-center py-12">
                <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Permits</h3>
                <p className="text-gray-600 mb-4">Create your first permit to get started</p>
                <Button onClick={() => setShowCreateForm(true)}>Create Permit</Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="types" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(permitTypes).map(([key, config]) => (
              <Card key={key}>
                <CardHeader>
                  <CardTitle className="flex items-center text-base">
                    {getPermitIcon(key)}
                    <span className="ml-2">{config.name}</span>
                  </CardTitle>
                  <CardDescription>{config.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    <p><strong>Max Duration:</strong> {config.max_duration_hours} hours</p>
                    <p><strong>Approvals Required:</strong> {config.required_approvals?.join(', ')}</p>
                    <div>
                      <strong>Default PPE:</strong>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {config.default_ppe?.map((ppe, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">{ppe}</Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Create Permit Dialog */}
      <Dialog open={showCreateForm} onOpenChange={setShowCreateForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Permit to Work</DialogTitle>
            <DialogDescription>Request authorization for high-risk work</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreatePermit} className="space-y-4">
            <div>
              <Label>Permit Type *</Label>
              <Select value={formData.permit_type} onValueChange={(v) => setFormData({...formData, permit_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(permitTypes).map(([key, config]) => (
                    <SelectItem key={key} value={key}>{config.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Title *</Label>
              <Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} required />
            </div>
            <div>
              <Label>Description *</Label>
              <Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} required rows={3} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Location *</Label>
                <Input value={formData.location} onChange={(e) => setFormData({...formData, location: e.target.value})} required />
              </div>
              <div>
                <Label>Project Name</Label>
                <Input value={formData.project_name} onChange={(e) => setFormData({...formData, project_name: e.target.value})} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Requestor Name *</Label>
                <Input value={formData.requestor_name} onChange={(e) => setFormData({...formData, requestor_name: e.target.value})} required />
              </div>
              <div>
                <Label>Supervisor *</Label>
                <Input value={formData.supervisor} onChange={(e) => setFormData({...formData, supervisor: e.target.value})} required />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Work Start *</Label>
                <Input type="datetime-local" value={formData.work_start} onChange={(e) => setFormData({...formData, work_start: e.target.value})} required />
              </div>
              <div>
                <Label>Work End *</Label>
                <Input type="datetime-local" value={formData.work_end} onChange={(e) => setFormData({...formData, work_end: e.target.value})} required />
              </div>
            </div>
            <div>
              <Label>Workers</Label>
              <div className="flex gap-2">
                <Input value={newWorker} onChange={(e) => setNewWorker(e.target.value)} placeholder="Worker name" />
                <Button type="button" onClick={addWorker}>Add</Button>
              </div>
              <div className="flex flex-wrap gap-1 mt-2">
                {formData.workers.map((w, idx) => (
                  <Badge key={idx} variant="outline">{w}</Badge>
                ))}
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setShowCreateForm(false)}>Cancel</Button>
              <Button type="submit">Create Permit</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Permit Detail Dialog */}
      <Dialog open={!!selectedPermit && !showApprovalDialog} onOpenChange={() => setSelectedPermit(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedPermit && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2">
                  {getPermitIcon(selectedPermit.permit_type)}
                  <Badge className={getStatusColor(selectedPermit.status)}>{selectedPermit.status?.replace('_', ' ')}</Badge>
                </div>
                <DialogTitle>{selectedPermit.title}</DialogTitle>
                <DialogDescription>{selectedPermit.permit_number}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <p>{selectedPermit.description}</p>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div><strong>Location:</strong> {selectedPermit.location}</div>
                  <div><strong>Supervisor:</strong> {selectedPermit.supervisor}</div>
                  <div><strong>Requestor:</strong> {selectedPermit.requestor_name}</div>
                  <div><strong>Workers:</strong> {selectedPermit.workers?.join(', ') || 'None'}</div>
                </div>
                <div className="text-sm">
                  <strong>Schedule:</strong><br />
                  {new Date(selectedPermit.work_start).toLocaleString()} - {new Date(selectedPermit.work_end).toLocaleString()}
                </div>
                {selectedPermit.ppe_required?.length > 0 && (
                  <div>
                    <strong>Required PPE:</strong>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {selectedPermit.ppe_required.map((ppe, idx) => (
                        <Badge key={idx} variant="outline">{ppe}</Badge>
                      ))}
                    </div>
                  </div>
                )}
                {selectedPermit.approvals?.length > 0 && (
                  <div>
                    <strong>Approvals:</strong>
                    <div className="space-y-1 mt-1">
                      {selectedPermit.approvals.map((a, idx) => (
                        <div key={idx} className="text-sm flex items-center gap-2">
                          {a.action === 'approve' ? <CheckCircle className="w-4 h-4 text-green-500" /> : <XCircle className="w-4 h-4 text-red-500" />}
                          {a.approver_name} ({a.approver_role})
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {selectedPermit.status === 'active' && (
                  <Button className="w-full" onClick={() => closePermit(selectedPermit.id)}>
                    Close Permit (Work Complete)
                  </Button>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Approval Dialog */}
      <Dialog open={showApprovalDialog} onOpenChange={setShowApprovalDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Review Permit</DialogTitle>
            <DialogDescription>{selectedPermit?.title}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Your Name *</Label>
              <Input value={approvalData.approver_name} onChange={(e) => setApprovalData({...approvalData, approver_name: e.target.value})} required />
            </div>
            <div>
              <Label>Your Role *</Label>
              <Select value={approvalData.approver_role} onValueChange={(v) => setApprovalData({...approvalData, approver_role: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="supervisor">Supervisor</SelectItem>
                  <SelectItem value="safety_officer">Safety Officer</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Comments</Label>
              <Textarea value={approvalData.comments} onChange={(e) => setApprovalData({...approvalData, comments: e.target.value})} />
            </div>
            <div className="flex gap-2">
              <Button className="flex-1 bg-green-600" onClick={() => { setApprovalData({...approvalData, action: 'approve'}); handleApproval(); }}>
                <CheckCircle className="w-4 h-4 mr-2" /> Approve
              </Button>
              <Button variant="destructive" className="flex-1" onClick={() => { setApprovalData({...approvalData, action: 'reject'}); handleApproval(); }}>
                <XCircle className="w-4 h-4 mr-2" /> Reject
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PermitToWorkPage;
